import React, { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

interface ImpactLevel {
  name: string
  modifier: number
  badgeBg: string
  badgeText: string
  cardBg: string
  cardBorder: string
  barColor: string
}

const LEVELS: ImpactLevel[] = [
  {
    name: 'Minor',
    modifier: 15,
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-700',
    cardBg: 'bg-slate-50/50',
    cardBorder: 'border-slate-200/60',
    barColor: 'bg-slate-400',
  },
  {
    name: 'Moderate',
    modifier: 30,
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    cardBg: 'bg-amber-50/30',
    cardBorder: 'border-amber-200/60',
    barColor: 'bg-amber-500',
  },
  {
    name: 'Major',
    modifier: 50,
    badgeBg: 'bg-orange-100',
    badgeText: 'text-orange-700',
    cardBg: 'bg-orange-50/30',
    cardBorder: 'border-orange-200/60',
    barColor: 'bg-orange-500',
  },
  {
    name: 'Extreme',
    modifier: 100,
    badgeBg: 'bg-rose-100',
    badgeText: 'text-rose-700',
    cardBg: 'bg-rose-50/30',
    cardBorder: 'border-rose-200/60',
    barColor: 'bg-rose-500',
  },
]

export default function ImpactLevelCompare() {
  const [baseRate, setBaseRate] = useState(100)

  const results = useMemo(() => {
    return LEVELS.map((level) => {
      const increase = Math.round(baseRate * (level.modifier / 100))
      const finalRate = baseRate + increase
      return { ...level, increase, finalRate }
    })
  }, [baseRate])

  const maxFinal = Math.max(...results.map((r) => r.finalRate))

  return (
    <div className="space-y-5">
      {/* Base rate input */}
      <div className="flex items-center gap-3">
        <Label htmlFor="impact-base-rate" className="text-sm font-medium text-foreground whitespace-nowrap">
          Base rate
        </Label>
        <div className="relative w-28">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
            £
          </span>
          <Input
            id="impact-base-rate"
            type="number"
            min={1}
            max={999}
            value={baseRate}
            onChange={(e) => setBaseRate(Math.max(1, Math.min(999, Number(e.target.value) || 1)))}
            className="h-9 pl-7 text-sm tabular-nums font-semibold"
          />
        </div>
        <span className="text-xs text-muted-foreground">per night before event adjustment</span>
      </div>

      {/* Impact columns */}
      <div className="grid grid-cols-4 gap-3">
        {results.map((level) => {
          const barPct = maxFinal > 0 ? (level.finalRate / maxFinal) * 100 : 0

          return (
            <div
              key={level.name}
              className={cn(
                'rounded-xl border p-4 transition-all duration-300 flex flex-col items-center text-center',
                level.cardBg,
                level.cardBorder
              )}
            >
              {/* Badge */}
              <span
                className={cn(
                  'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize',
                  level.badgeBg,
                  level.badgeText
                )}
              >
                {level.name}
              </span>

              {/* Modifier */}
              <span className="mt-3 text-xs font-medium text-muted-foreground">
                +{level.modifier}%
              </span>

              {/* Calculation */}
              <div className="mt-2 space-y-0.5">
                <span className="block text-[11px] text-muted-foreground tabular-nums">
                  £{baseRate} + £{level.increase}
                </span>
                <span className="block text-lg font-bold tabular-nums text-foreground tracking-tight">
                  £{level.finalRate}
                </span>
              </div>

              {/* Magnitude bar */}
              <div className="mt-3 w-full">
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all duration-500 ease-out', level.barColor)}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary line */}
      <p className="text-xs text-muted-foreground text-center">
        An <strong className="text-rose-700">extreme</strong> event adds{' '}
        <span className="tabular-nums font-semibold text-foreground">
          £{results[3].increase}
        </span>{' '}
        more per night than the base rate — {results[3].modifier}% above normal pricing.
      </p>
    </div>
  )
}
